import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Avatar,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip
} from '@mui/material';
import {
  Business as BusinessIcon,
  CloudUpload as UploadIcon,
  Save as SaveIcon,
  Palette as PaletteIcon
} from '@mui/icons-material';
import api from '../services/api';
import { loadCompanyProfile, saveCompanyProfile } from '../utils/companyProfileStorage';
import { ACTIVITY_MODES, loadActivityMode, saveActivityMode } from '../utils/activityModeStorage';

const hexToRgb = (hexColor) => {
  const normalized = (hexColor || '').replace('#', '');
  if (!/^[0-9A-Fa-f]{6}$/.test(normalized)) return null;
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16)
  };
};

const isVeryLightColor = (hexColor) => {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return false;
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance >= 0.82;
};

const CompanyProfile = () => {
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activityId, setActivityId] = useState(() => loadActivityMode()?.id || '');
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    address: '',
    postalCode: '',
    city: '',
    country: 'France',
    phone: '',
    email: '',
    siret: '',
    vatNumber: '',
    vatLiable: true,
    defaultVatRate: 20,
    website: '',
    logo: '',
    primaryColor: '#A7AFB9',
    secondaryColor: '#1F2937',
    industry: 'services',
    beautySpecialties: [],
    documentTheme: 'modern',
    currency: 'EUR',
    catalogItems: [],
    quoteColumns: ['reference', 'description', 'quantity', 'unitPrice', 'vatRate', 'totalHT', 'totalTTC']
  });
  const currentUser = React.useMemo(() => {
    try {
      const rawUser = localStorage.getItem('user');
      return rawUser ? JSON.parse(rawUser) : null;
    } catch (error) {
      return null;
    }
  }, []);
  const canEditCompanyIdentity = currentUser?.role === 'ADMIN' || !currentUser?.accountId;

  const [beautySpecialtiesMenuOpen, setBeautySpecialtiesMenuOpen] = useState(false);

  // Colonnes disponibles pour les devis/factures
  const availableColumns = [
    { id: 'reference', label: 'Référence', mandatory: false },
    { id: 'description', label: 'Description', mandatory: true },
    { id: 'quantity', label: 'Quantité', mandatory: false },
    { id: 'unit', label: 'Unité (h, j, pcs...)', mandatory: false },
    { id: 'unitPrice', label: 'Prix unitaire', mandatory: false },
    { id: 'discount', label: 'Remise (%)', mandatory: false },
    { id: 'vatRate', label: 'TVA (%)', mandatory: false },
    { id: 'totalHT', label: 'Total HT', mandatory: false },
    { id: 'totalTTC', label: 'Total TTC', mandatory: false },
    { id: 'deliveryDate', label: 'Date de livraison', mandatory: false }
  ];

  const industries = [
    { value: 'services', label: 'Services / Consulting' },
    { value: 'it', label: 'Informatique / IT' },
    { value: 'construction', label: 'BTP / Construction' },
    { value: 'beauty', label: 'Beauté / Esthétique' },
    { value: 'commerce', label: 'Commerce / Vente' },
    { value: 'artisan', label: 'Artisan' },
    { value: 'liberal', label: 'Profession Libérale' },
    { value: 'industrie', label: 'Industrie' },
    { value: 'other', label: 'Autre' }
  ];

  const documentThemeOptions = [
    { value: 'modern', label: 'Moderne' },
    { value: 'classic', label: 'Classique' },
    { value: 'minimal', label: 'Minimal' }
  ];

  const beautySpecialtyOptions = [
    { value: 'onglerie', label: 'Onglerie' },
    { value: 'massages', label: 'Massages' },
    { value: 'institut', label: 'Institut de beauté' },
    { value: 'coiffeur', label: 'Coiffeur / Coiffure' },
    { value: 'cils', label: 'Pose de cils' },
    { value: 'epilation', label: 'Épilation' },
    { value: 'maquillage', label: 'Maquillage' },
    { value: 'soins-visage', label: 'Soins visage' }
  ];

  useEffect(() => {
    // Charger le profil depuis l'API
    const loadProfile = async () => {
      try {
        const response = await api.get('/auth/profile');
        const userData = response.data.user;
        
        // Fusionner avec les données du localStorage (pour les colonnes de devis et couleurs)
        const localData = loadCompanyProfile() || {};
        
        const merged = {
          name: userData.companyName || '',
          address: userData.address || '',
          postalCode: userData.postalCode || '',
          city: userData.city || '',
          country: userData.country || 'France',
          phone: userData.phone || '',
          email: userData.email || '',
          siret: userData.siret || '',
          vatNumber: userData.vatNumber || '',
          vatLiable:
            userData.vatLiable !== undefined && userData.vatLiable !== null
              ? userData.vatLiable
              : localData.vatLiable !== undefined
                ? localData.vatLiable
                : true,
          defaultVatRate:
            userData.defaultVatRate !== undefined && userData.defaultVatRate !== null
              ? userData.defaultVatRate
              : localData.defaultVatRate !== undefined
                ? localData.defaultVatRate
                : 20,
          logo: userData.logo || localData.logo || '',
          legalStatus: userData.legalStatus || '',
          industry: userData.industry || localData.industry || 'services',
          beautySpecialties: localData.beautySpecialties || [],
          currency: localData.currency || 'EUR',
          primaryColor: userData.primaryColor || localData.primaryColor || '#977F6B',
          secondaryColor: userData.secondaryColor || localData.secondaryColor || '#5E564F',
          website: localData.website || '',
          quoteColumns: localData.quoteColumns || ['reference', 'description', 'quantity', 'unitPrice', 'vatRate', 'totalHT', 'totalTTC'],
          catalogItems: localData.catalogItems || [],
          documentTheme: userData.documentTheme || localData.documentTheme || 'modern',
        };
        
        // S'assurer que description est toujours inclus
        if (!merged.quoteColumns.includes('description')) {
          merged.quoteColumns = ['description', ...merged.quoteColumns];
        }
        
        setCompanyInfo(merged);
        try {
          const existingActivity = loadActivityMode();
          setActivityId(existingActivity?.id || '');
        } catch {
          setActivityId('');
        }
      } catch (error) {
        console.error('Erreur chargement profil:', error);
        // Fallback sur localStorage
        const parsed = loadCompanyProfile();
        if (parsed) {
          if (parsed.vatLiable === undefined) {
            parsed.vatLiable = true;
          }
          if (parsed.defaultVatRate === undefined) {
            parsed.defaultVatRate = 20;
          }
          if (!parsed.quoteColumns || parsed.quoteColumns.length === 0) {
            parsed.quoteColumns = ['reference', 'description', 'quantity', 'unitPrice', 'vatRate', 'totalHT', 'totalTTC'];
          } else if (!parsed.quoteColumns.includes('description')) {
            parsed.quoteColumns = ['description', ...parsed.quoteColumns];
          }
          setCompanyInfo(parsed);
          try {
            const existingActivity = loadActivityMode();
            setActivityId(existingActivity?.id || '');
          } catch {
            setActivityId('');
          }
        }
      }
    };
    
    loadProfile();
  }, []);

  useEffect(() => {
    const isTradeIndustry = companyInfo.industry === 'artisan' || companyInfo.industry === 'construction';
    if (!isTradeIndustry) {
      setActivityId('');
    }
  }, [companyInfo.industry]);

  const tradeActivityOptions = React.useMemo(() => {
    const allowed = new Set(['electrician', 'plumber', 'construction']);
    return ACTIVITY_MODES.filter((m) => allowed.has(m.id));
  }, []);

  const handleActivityChange = async (newId) => {
    setActivityId(newId);
    const meta = ACTIVITY_MODES.find((m) => m.id === newId) || null;
    if (!meta) return;

    saveActivityMode({ id: meta.id, label: meta.label, presetCatalogId: meta.presetCatalogId, updatedAt: new Date().toISOString() });
    window.dispatchEvent(new Event('activityModeUpdated'));

    try {
      await api.post('/analytics/onboarding/activity', { activity: meta.id });
    } catch {
      // best-effort
    }
  };

  const handleChange = (field, value) => {
    setCompanyInfo(prev => {
      const updated = {
        ...prev,
        [field]: value
      };

      // Appliquer immédiatement les couleurs dans toute l'app (preview live)
      if ((field === 'primaryColor' || field === 'secondaryColor') && canEditCompanyIdentity) {
        try {
          saveCompanyProfile(updated);
          window.dispatchEvent(new Event('companyProfileUpdated'));
        } catch {
          // best-effort
        }
      }

      return updated;
    });
  };

  const [newCatalogItem, setNewCatalogItem] = useState({
    label: '',
    description: '',
    unit: 'unité',
    unitPrice: '',
    vatRate: 20
  });

  const isPrimaryVeryLight = isVeryLightColor(companyInfo.primaryColor);
  const textOnPrimary = isPrimaryVeryLight ? '#1F2937' : '#FFFFFF';
  const previewBackground = isPrimaryVeryLight ? '#2F2A26' : '#FFFFFF';
  const previewTextColor = isPrimaryVeryLight ? 'rgba(255,255,255,0.92)' : 'text.primary';

  const handleAddCatalogItem = () => {
    if (!newCatalogItem.label) return;

    const item = {
      id: `custom-${Date.now()}`,
      label: newCatalogItem.label,
      description: newCatalogItem.description || newCatalogItem.label,
      unit: newCatalogItem.unit || 'unité',
      unitPrice: parseFloat(newCatalogItem.unitPrice) || 0,
      vatRate: parseFloat(newCatalogItem.vatRate) || 20
    };

    setCompanyInfo(prev => ({
      ...prev,
      catalogItems: [...(prev.catalogItems || []), item]
    }));

    setNewCatalogItem({
      label: '',
      description: '',
      unit: 'unité',
      unitPrice: '',
      vatRate: 20
    });
  };

  const handleRemoveCatalogItem = (itemId) => {
    setCompanyInfo(prev => ({
      ...prev,
      catalogItems: (prev.catalogItems || []).filter(item => item.id !== itemId)
    }));
  };

  const handleLogoUpload = async (event) => {
    if (!canEditCompanyIdentity) {
      setMessage({ type: 'info', text: 'Seul le responsable du compte peut modifier le logo/couleurs/thème de l\'entreprise.' });
      return;
    }
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Le logo ne doit pas dépasser 2 Mo' });
        return;
      }

      const extractColorsFromLogo = (imageFile) => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const size = 64;
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, size, size);
            const { data } = ctx.getImageData(0, 0, size, size);
            let r = 0;
            let g = 0;
            let b = 0;
            let count = 0;
            for (let i = 0; i < data.length; i += 4) {
              const alpha = data[i + 3];
              if (alpha < 50) continue;
              r += data[i];
              g += data[i + 1];
              b += data[i + 2];
              count += 1;
            }
            if (!count) {
              resolve(null);
              return;
            }
            r = Math.round(r / count);
            g = Math.round(g / count);
            b = Math.round(b / count);

            const toHex = (value) => value.toString(16).padStart(2, '0');
            const primary = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
            const darken = (value) => Math.max(0, Math.round(value * 0.55));
            const secondary = `#${toHex(darken(r))}${toHex(darken(g))}${toHex(darken(b))}`;
            resolve({ primary, secondary });
          };
          img.onerror = () => resolve(null);
          img.src = e.target.result;
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(imageFile);
      });

      const normalizeExtractedColors = (colors) => {
        if (!colors?.primary || !colors?.secondary) return null;

        const hexToRgb = (hex) => {
          const value = String(hex).replace('#', '').trim();
          if (value.length !== 6) return null;
          const r = parseInt(value.slice(0, 2), 16);
          const g = parseInt(value.slice(2, 4), 16);
          const b = parseInt(value.slice(4, 6), 16);
          if ([r, g, b].some((c) => Number.isNaN(c))) return null;
          return { r, g, b };
        };

        // Relative luminance (WCAG)
        const luminance = ({ r, g, b }) => {
          const srgb = [r, g, b].map((v) => v / 255);
          const lin = srgb.map((c) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
          return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
        };

        const primaryRgb = hexToRgb(colors.primary);
        const secondaryRgb = hexToRgb(colors.secondary);
        if (!primaryRgb || !secondaryRgb) return null;

        const primaryLum = luminance(primaryRgb);
        const secondaryLum = luminance(secondaryRgb);

        // Fallback to current/default theme if extracted colors are too dark (common with dark logos)
        const fallbackPrimary = (companyInfo?.primaryColor || '#A7AFB9').trim();
        const fallbackSecondary = (companyInfo?.secondaryColor || '#1F2937').trim();

        return {
          primary: primaryLum < 0.18 ? fallbackPrimary : colors.primary,
          secondary: secondaryLum < 0.06 ? fallbackSecondary : colors.secondary,
        };
      };

      try {
        // Uploader le logo via l'API
        const formData = new FormData();
        formData.append('logo', file);
        
        const response = await api.post('/auth/profile/logo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Mettre à jour l'état avec le chemin du logo
        const logoUrl = response.data.logoUrl;
        handleChange('logo', logoUrl);

        const rawColors = await extractColorsFromLogo(file);
        const colors = rawColors ? normalizeExtractedColors(rawColors) : null;
        if (colors) {
          const updatedInfo = {
            ...companyInfo,
            logo: logoUrl,
            primaryColor: colors.primary,
            secondaryColor: colors.secondary
          };
          setCompanyInfo(updatedInfo);
          saveCompanyProfile(updatedInfo);
          window.dispatchEvent(new Event('companyProfileUpdated'));

          // Persister aussi côté serveur pour conserver après déconnexion/reconnexion
          try {
            await api.put('/auth/profile', {
              primaryColor: colors.primary,
              secondaryColor: colors.secondary,
            });
          } catch (persistError) {
            // Non bloquant: le localStorage reste une sauvegarde, et l'utilisateur peut enregistrer le profil.
            console.warn('Impossible de persister les couleurs côté serveur:', persistError);
          }
        }
        setMessage({ type: 'success', text: 'Logo uploadé avec succès' });
      } catch (error) {
        console.error('Erreur upload logo:', error);
        const apiError = error?.response?.data?.message || error?.response?.data?.error;
        setMessage({
          type: 'error',
          text: apiError || "Erreur lors de l'upload du logo"
        });
      }
    }
  };

  const handleSave = async () => {
    if (!canEditCompanyIdentity) {
      setMessage({ type: 'info', text: 'Seul le responsable du compte peut enregistrer/modifier l\'identité et le thème de l\'entreprise.' });
      return;
    }
    // S'assurer que 'description' est toujours incluse dans quoteColumns
    const updatedInfo = {
      ...companyInfo,
      quoteColumns: companyInfo.quoteColumns && companyInfo.quoteColumns.includes('description')
        ? companyInfo.quoteColumns
        : ['description', ...(companyInfo.quoteColumns || [])],
      vatNumber: (companyInfo.vatNumber || '').trim()
    };

    if (updatedInfo.vatLiable && !updatedInfo.vatNumber) {
      setMessage({ type: 'error', text: 'Le numéro de TVA est obligatoire lorsque l\'entreprise est soumise à la TVA.' });
      return;
    }

    const payload = {
      companyName: updatedInfo.name || '',
      legalStatus: updatedInfo.legalStatus || '',
      industry: updatedInfo.industry || 'services',
      siret: updatedInfo.siret || '',
      address: updatedInfo.address || '',
      postalCode: updatedInfo.postalCode || '',
      city: updatedInfo.city || '',
      country: updatedInfo.country || 'France',
      phone: updatedInfo.phone || '',
      vatNumber: updatedInfo.vatNumber || '',
      vatLiable: !!updatedInfo.vatLiable,
      defaultVatRate: Number(updatedInfo.defaultVatRate) || 0,
      primaryColor: updatedInfo.primaryColor || '#977F6B',
      secondaryColor: updatedInfo.secondaryColor || '#5E564F',
      documentTheme: updatedInfo.documentTheme || 'modern'
    };

    try {
      // Sauvegarder dans l'API
      await api.put('/auth/profile', payload);

      // Sauvegarder aussi dans localStorage (pour couleurs et colonnes)
      saveCompanyProfile(updatedInfo);
      setCompanyInfo(updatedInfo);
      window.dispatchEvent(new Event('companyProfileUpdated'));
      setMessage({ type: 'success', text: 'Profil entreprise enregistré avec succès !' });

      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Erreur sauvegarde:', error);

      // Sauvegarde locale pour éviter toute perte de données
      saveCompanyProfile(updatedInfo);
      setCompanyInfo(updatedInfo);
      window.dispatchEvent(new Event('companyProfileUpdated'));

      const apiError = error?.response?.data?.error || error?.response?.data?.message || 'Erreur lors de l\'enregistrement';
      setMessage({ type: 'warning', text: `Enregistré localement. Détail: ${apiError}` });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon /> Profil de l'Entreprise
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configurez les informations de votre entreprise pour personnaliser vos devis et factures
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      {!canEditCompanyIdentity && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Mode équipe : l'identité (logo/couleurs/thème) est partagée. Seul le responsable du compte peut la modifier.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Logo et Branding */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PaletteIcon /> Votre Marque
              </Typography>
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Thème des documents</InputLabel>
                <Select
                  value={companyInfo.documentTheme || 'modern'}
                  onChange={(e) => handleChange('documentTheme', e.target.value)}
                  label="Thème des documents"
                  disabled={!canEditCompanyIdentity}
                >
                  {documentThemeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                  {canEditCompanyIdentity
                    ? 'Définit le rendu des devis/factures pour toute l\'équipe.'
                    : 'Seul le responsable du compte peut modifier le thème.'}
                </Typography>
              </FormControl>
              
              <Box sx={{ textAlign: 'center', my: 3 }}>
                <Avatar
                  src={companyInfo.logo}
                  sx={{
                    width: 150,
                    height: 150,
                    margin: '0 auto',
                    bgcolor: companyInfo.primaryColor,
                    color: textOnPrimary,
                    fontSize: '3rem'
                  }}
                >
                  {!companyInfo.logo && (companyInfo.name?.[0] || 'E')}
                </Avatar>
                
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadIcon />}
                  sx={{ mt: 2 }}
                  disabled={!canEditCompanyIdentity}
                >
                  Changer le logo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                </Button>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Format: PNG, JPG (Max 2 Mo)
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <TextField
                fullWidth
                label="Couleur Principale"
                type="color"
                value={companyInfo.primaryColor}
                onChange={(e) => handleChange('primaryColor', e.target.value)}
                sx={{ mb: 2 }}
                disabled={!canEditCompanyIdentity}
              />

              <TextField
                fullWidth
                label="Couleur Secondaire"
                type="color"
                value={companyInfo.secondaryColor}
                onChange={(e) => handleChange('secondaryColor', e.target.value)}
                disabled={!canEditCompanyIdentity}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Informations entreprise */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Informations Légales
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Nom de l'entreprise"
                    value={companyInfo.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Secteur d'activité</InputLabel>
                    <Select
                      value={companyInfo.industry}
                      onChange={(e) => handleChange('industry', e.target.value)}
                      label="Secteur d'activité"
                    >
                      {industries.map(ind => (
                        <MenuItem key={ind.value} value={ind.value}>
                          {ind.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {(companyInfo.industry === 'artisan' || companyInfo.industry === 'construction') && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Métier</InputLabel>
                      <Select
                        value={activityId}
                        onChange={(e) => handleActivityChange(e.target.value)}
                        label="Métier"
                      >
                        {tradeActivityOptions.map((mode) => (
                          <MenuItem key={mode.id} value={mode.id}>
                            {mode.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75 }}>
                        Active un catalogue et des modèles adaptés à votre métier (devis/factures pré-remplis).
                      </Typography>
                    </FormControl>
                  </Grid>
                )}

                {companyInfo.industry === 'beauty' && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Spécialités beauté</InputLabel>
                      <Select
                        multiple
                        open={beautySpecialtiesMenuOpen}
                        onOpen={() => setBeautySpecialtiesMenuOpen(true)}
                        onClose={() => setBeautySpecialtiesMenuOpen(false)}
                        value={companyInfo.beautySpecialties || []}
                        onChange={(e) => {
                          handleChange('beautySpecialties', e.target.value);
                          setBeautySpecialtiesMenuOpen(false);
                        }}
                        label="Spécialités beauté"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => {
                              const option = beautySpecialtyOptions.find((item) => item.value === value);
                              return <Chip key={value} label={option?.label || value} size="small" />;
                            })}
                          </Box>
                        )}
                      >
                        {beautySpecialtyOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Devise</InputLabel>
                    <Select
                      value={companyInfo.currency}
                      onChange={(e) => handleChange('currency', e.target.value)}
                      label="Devise"
                    >
                      <MenuItem value="EUR">Euro (€)</MenuItem>
                      <MenuItem value="USD">Dollar ($)</MenuItem>
                      <MenuItem value="GBP">Livre (£)</MenuItem>
                      <MenuItem value="CHF">Franc Suisse (CHF)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Adresse"
                    value={companyInfo.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Code Postal"
                    value={companyInfo.postalCode}
                    onChange={(e) => handleChange('postalCode', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Ville"
                    value={companyInfo.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Pays"
                    value={companyInfo.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Téléphone"
                    value={companyInfo.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={companyInfo.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="SIRET"
                    value={companyInfo.siret}
                    onChange={(e) => handleChange('siret', e.target.value)}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Numéro de TVA"
                    required={companyInfo.vatLiable}
                    value={companyInfo.vatNumber}
                    onChange={(e) => handleChange('vatNumber', e.target.value)}
                    error={companyInfo.vatLiable && !companyInfo.vatNumber?.trim()}
                    helperText={companyInfo.vatLiable && !companyInfo.vatNumber?.trim() ? 'Obligatoire si soumis à la TVA' : ''}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Soumis à la TVA ?</InputLabel>
                    <Select
                      label="Soumis à la TVA ?"
                      value={companyInfo.vatLiable ? 'yes' : 'no'}
                      onChange={(e) => {
                        const liable = e.target.value === 'yes';
                        handleChange('vatLiable', liable);
                        if (!liable) {
                          handleChange('defaultVatRate', 0);
                        } else if (companyInfo.defaultVatRate === 0) {
                          handleChange('defaultVatRate', 20);
                        }
                      }}
                    >
                      <MenuItem value="yes">Oui</MenuItem>
                      <MenuItem value="no">Non</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={!companyInfo.vatLiable}>
                    <InputLabel>TVA par défaut</InputLabel>
                    <Select
                      label="TVA par défaut"
                      value={companyInfo.vatLiable ? companyInfo.defaultVatRate : 0}
                      onChange={(e) => handleChange('defaultVatRate', e.target.value)}
                    >
                      <MenuItem value={5.5}>5,5%</MenuItem>
                      <MenuItem value={20}>20%</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Site Web"
                    value={companyInfo.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  size="large"
                  disabled={!canEditCompanyIdentity}
                >
                  Enregistrer le Profil
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Configuration des colonnes */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon />
                Configuration des Colonnes (Devis & Factures)
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Choisissez les colonnes à afficher dans vos devis et factures.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    handleChange('quoteColumns', ['reference', 'description', 'quantity', 'unitPrice', 'vatRate', 'totalHT', 'totalTTC']);
                    setMessage({ type: 'info', text: 'Configuration réinitialisée aux valeurs par défaut' });
                    setTimeout(() => setMessage({ type: '', text: '' }), 2000);
                  }}
                >
                  Réinitialiser
                </Button>
              </Box>

              <Grid container spacing={2}>
                {availableColumns.map((column) => (
                  <Grid item xs={12} sm={6} md={4} key={column.id}>
                    <Box
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: companyInfo.quoteColumns?.includes(column.id) ? companyInfo.primaryColor : 'grey.300',
                        borderRadius: 2,
                        bgcolor: companyInfo.quoteColumns?.includes(column.id) ? `${companyInfo.primaryColor}15` : 'white',
                        cursor: column.mandatory ? 'not-allowed' : 'pointer',
                        opacity: column.mandatory ? 0.7 : 1,
                        transition: 'all 0.3s',
                        '&:hover': {
                          borderColor: column.mandatory ? 'grey.300' : companyInfo.primaryColor,
                          transform: column.mandatory ? 'none' : 'translateY(-2px)'
                        }
                      }}
                      onClick={() => {
                        if (!column.mandatory) {
                          const currentColumns = companyInfo.quoteColumns || [];
                          const newColumns = currentColumns.includes(column.id)
                            ? currentColumns.filter(c => c !== column.id)
                            : [...currentColumns, column.id];
                          handleChange('quoteColumns', newColumns);
                        }
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight="bold">
                        {column.label}
                      </Typography>
                      {column.mandatory && (
                        <Chip label="Obligatoire" size="small" color="primary" sx={{ mt: 1 }} />
                      )}
                      {!column.mandatory && companyInfo.quoteColumns?.includes(column.id) && (
                        <Chip label="Activée" size="small" sx={{ mt: 1, bgcolor: companyInfo.primaryColor, color: textOnPrimary }} />
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Alert severity="info" sx={{ mt: 3 }}>
                <strong>Colonnes sélectionnées ({companyInfo.quoteColumns?.length || 0}) :</strong><br />
                {companyInfo.quoteColumns?.map(colId => 
                  availableColumns.find(c => c.id === colId)?.label
                ).join(', ') || 'Aucune colonne sélectionnée'}
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Catalogue personnalisé */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Catalogue personnalisé (cases à cocher)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ajoutez vos prestations pour les retrouver en un clic dans les devis/factures.
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Libellé"
                    value={newCatalogItem.label}
                    onChange={(e) => setNewCatalogItem({ ...newCatalogItem, label: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={newCatalogItem.description}
                    onChange={(e) => setNewCatalogItem({ ...newCatalogItem, description: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Unité</InputLabel>
                    <Select
                      label="Unité"
                      value={newCatalogItem.unit}
                      onChange={(e) => setNewCatalogItem({ ...newCatalogItem, unit: e.target.value })}
                    >
                      <MenuItem value="unité">unité</MenuItem>
                      <MenuItem value="h">heure</MenuItem>
                      <MenuItem value="j">jour</MenuItem>
                      <MenuItem value="mois">mois</MenuItem>
                      <MenuItem value="forfait">forfait</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6} md={1}>
                  <TextField
                    fullWidth
                    label="Prix"
                    type="number"
                    value={newCatalogItem.unitPrice}
                    onChange={(e) => setNewCatalogItem({ ...newCatalogItem, unitPrice: e.target.value })}
                  />
                </Grid>
                <Grid item xs={6} md={1}>
                  <FormControl fullWidth>
                    <InputLabel>TVA</InputLabel>
                    <Select
                      label="TVA"
                      value={newCatalogItem.vatRate}
                      onChange={(e) => setNewCatalogItem({ ...newCatalogItem, vatRate: e.target.value })}
                    >
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={5.5}>5,5%</MenuItem>
                      <MenuItem value={10}>10%</MenuItem>
                      <MenuItem value={20}>20%</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleAddCatalogItem}
                >
                  Ajouter au catalogue
                </Button>
              </Box>

              {companyInfo.catalogItems?.length ? (
                <Grid container spacing={1}>
                  {companyInfo.catalogItems.map((item) => (
                    <Grid item xs={12} md={6} key={item.id}>
                      <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography fontWeight="bold">{item.label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.unitPrice} € / {item.unit} • TVA {item.vatRate}%
                          </Typography>
                        </Box>
                        <Button color="error" onClick={() => handleRemoveCatalogItem(item.id)}>
                          Supprimer
                        </Button>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aucun élément personnalisé pour le moment.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Aperçu */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h6" gutterBottom>
              Aperçu de vos documents
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Votre logo et vos couleurs apparaîtront sur tous vos devis et factures
            </Typography>
            <Box sx={{ mt: 2, p: 2, bgcolor: previewBackground, borderRadius: 2, border: '2px dashed', borderColor: companyInfo.primaryColor }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {companyInfo.logo && (
                  <img src={companyInfo.logo} alt="Logo" style={{ height: 80 }} />
                )}
                <Box>
                  <Typography variant="h6" sx={{ color: companyInfo.primaryColor }}>
                    {companyInfo.name || 'Votre Entreprise'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: previewTextColor }}>
                    {companyInfo.address && `${companyInfo.address}, `}
                    {companyInfo.postalCode} {companyInfo.city}
                  </Typography>
                  <Typography variant="body2" sx={{ color: previewTextColor }}>
                    {companyInfo.phone} • {companyInfo.email}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompanyProfile;
